/**
 * LoRa network profiles: the radio settings a user's radio must match to hear
 * a given mesh.
 *
 * Default: Sacramento's community MeshCore mesh (909.875 MHz / 62.5 kHz / SF9 /
 * CR5). Every value is user-settable; Meshtastic and other regions are
 * selectable; a custom profile is validated before it can touch a radio.
 *
 * Region bands and regional presets are copied from MeshBell's vetted table
 * (fixtures/lora-regions.json), which cites a regulator and the MeshCore
 * config API for every row. Each profile below records where its numbers came
 * from and when they were last checked: community presets drift, and MeshBell
 * learned that a "community preset" claim nobody re-checks goes stale.
 *
 * Legality is not optional. A profile whose channel (centre +/- half the
 * bandwidth) leaves its region's band is refused, and the radio session
 * refuses to transmit when the radio REPORTS a band outside the chosen region,
 * because MeshCore firmware ships on EU 869 MHz, which is cellular downlink in
 * the United States.
 */

export interface LoRaRegion {
  code: string
  displayName: string
  /** Inclusive legal band, MHz. */
  bandMhz: readonly [number, number]
  /** Max transmit duty cycle, percent; null = no limit. */
  dutyCyclePct: number | null
}

export const REGIONS: Record<string, LoRaRegion> = {
  us_915: { code: 'us_915', displayName: 'United States and Canada (915 MHz)', bandMhz: [902, 928], dutyCyclePct: null },
  eu_868: { code: 'eu_868', displayName: 'Europe and United Kingdom (868 MHz)', bandMhz: [869.4, 869.65], dutyCyclePct: 10 },
  au_915: { code: 'au_915', displayName: 'Australia (915 MHz)', bandMhz: [915, 928], dutyCyclePct: null },
  nz_915: { code: 'nz_915', displayName: 'New Zealand (915 MHz)', bandMhz: [915, 928], dutyCyclePct: null },
}

/** Bandwidths the SX126x / SX127x radios accept, kHz. */
export const LORA_BANDWIDTHS_KHZ = [7.8, 10.4, 15.6, 20.8, 31.25, 41.7, 62.5, 125, 250, 500] as const

export type MeshtasticModemPreset =
  | 'LONG_FAST'
  | 'LONG_SLOW'
  | 'LONG_MODERATE'
  | 'MEDIUM_FAST'
  | 'MEDIUM_SLOW'
  | 'SHORT_FAST'
  | 'SHORT_SLOW'

interface ProfileBase {
  id: string
  label: string
  region: string
  /** Where the numbers came from, and when they were last checked. */
  source: string
  checked: string
  origin: 'built-in' | 'custom' | 'room'
}

export interface MeshCoreProfile extends ProfileBase {
  firmware: 'meshcore'
  freqMHz: number
  bwKHz: number
  sf: number
  cr: number
}

export interface MeshtasticProfile extends ProfileBase {
  firmware: 'meshtastic'
  /** Meshtastic region code, e.g. "US", "EU_868". */
  meshtasticRegion: string
  modemPreset: MeshtasticModemPreset
}

export type LoRaNetworkProfile = MeshCoreProfile | MeshtasticProfile

export const DEFAULT_PROFILE_ID = 'meshcore-us-sacramento'

export const BUILT_IN_PROFILES: readonly LoRaNetworkProfile[] = [
  {
    id: 'meshcore-us-sacramento',
    label: 'Sacramento community mesh (MeshCore)',
    firmware: 'meshcore',
    region: 'us_915',
    freqMHz: 909.875,
    bwKHz: 62.5,
    sf: 9,
    cr: 5,
    source:
      "West Coast Mesh Discord, Sacramento section channel '909·875—62·5—9—5'; a Heltec V4 on it held 350 contacts (MeshBell bench) and reported these exact values to CrowdTalkie's client",
    checked: '2026-10-14',
    origin: 'built-in',
  },
  {
    id: 'meshcore-us-recommended',
    label: 'USA / Canada recommended (MeshCore)',
    firmware: 'meshcore',
    region: 'us_915',
    freqMHz: 910.525,
    bwKHz: 62.5,
    sf: 7,
    cr: 5,
    source: 'docs.meshcore.io FAQ US preset; MeshBell us_915 row. NOT what the Sacramento community runs',
    checked: '2026-10-14',
    origin: 'built-in',
  },
  {
    id: 'meshcore-eu-uk',
    label: 'Europe / UK (MeshCore)',
    firmware: 'meshcore',
    region: 'eu_868',
    freqMHz: 869.618,
    bwKHz: 62.5,
    sf: 8,
    cr: 8,
    source: 'api.meshcore.nz/api/v1/config via MeshBell eu_868 row (CEPT ERC/REC 70-03 band 869.4-869.65)',
    checked: '2026-10-14',
    origin: 'built-in',
  },
  {
    id: 'meshcore-au',
    label: 'Australia (MeshCore)',
    firmware: 'meshcore',
    region: 'au_915',
    freqMHz: 916.575,
    bwKHz: 62.5,
    sf: 7,
    cr: 8,
    source: 'api.meshcore.nz/api/v1/config via MeshBell au_915 row',
    checked: '2026-10-14',
    origin: 'built-in',
  },
  {
    id: 'meshcore-nz',
    label: 'New Zealand (MeshCore)',
    firmware: 'meshcore',
    region: 'nz_915',
    freqMHz: 917.375,
    bwKHz: 62.5,
    sf: 7,
    cr: 5,
    source: 'api.meshcore.nz/api/v1/config via MeshBell nz_915 row',
    checked: '2026-10-14',
    origin: 'built-in',
  },
  {
    id: 'meshtastic-us-longfast',
    label: 'USA LongFast (Meshtastic default)',
    firmware: 'meshtastic',
    region: 'us_915',
    meshtasticRegion: 'US',
    modemPreset: 'LONG_FAST',
    source: 'Meshtastic default modem preset; frequency slot derives from the primary channel name',
    checked: '2026-10-14',
    origin: 'built-in',
  },
  {
    id: 'meshtastic-us-mediumfast',
    label: 'USA MediumFast (Meshtastic)',
    firmware: 'meshtastic',
    region: 'us_915',
    meshtasticRegion: 'US',
    modemPreset: 'MEDIUM_FAST',
    source: 'Meshtastic modem preset used by busier US meshes',
    checked: '2026-10-14',
    origin: 'built-in',
  },
  {
    id: 'meshtastic-eu-longfast',
    label: 'Europe 868 LongFast (Meshtastic)',
    firmware: 'meshtastic',
    region: 'eu_868',
    meshtasticRegion: 'EU_868',
    modemPreset: 'LONG_FAST',
    source: 'Meshtastic default modem preset, EU_868 region',
    checked: '2026-10-14',
    origin: 'built-in',
  },
]

export interface ProfileProblem {
  field: string
  detail: string
}

/** The occupied channel, MHz: centre +/- half the bandwidth. */
export function channelEdges(freqMHz: number, bwKHz: number): [number, number] {
  const half = bwKHz / 2000
  return [freqMHz - half, freqMHz + half]
}

/** Is a radio reporting these settings inside the region's legal band? */
export function isInsideBand(regionCode: string, freqMHz: number, bwKHz: number): boolean {
  const region = REGIONS[regionCode]
  if (!region) return false
  const [lo, hi] = channelEdges(freqMHz, bwKHz)
  return lo >= region.bandMhz[0] && hi <= region.bandMhz[1]
}

/** Everything wrong with a profile, or [] if it may be applied to a radio. */
export function validateProfile(p: LoRaNetworkProfile): ProfileProblem[] {
  const problems: ProfileProblem[] = []
  const region = REGIONS[p.region]
  if (!region) {
    return [{ field: 'region', detail: `unknown region "${p.region}"` }]
  }
  if (p.firmware === 'meshtastic') {
    if (!p.meshtasticRegion) problems.push({ field: 'meshtasticRegion', detail: 'required' })
    return problems
  }

  if (!Number.isFinite(p.freqMHz)) {
    problems.push({ field: 'freqMHz', detail: 'not a number' })
  } else if (!Number.isInteger(Math.round(p.freqMHz * 1000 * 1000) / 1000)) {
    // The firmware takes whole kHz (meshcore_py truncates the rest silently).
    problems.push({ field: 'freqMHz', detail: 'must be a whole number of kHz (3 decimals in MHz)' })
  }
  if (!(LORA_BANDWIDTHS_KHZ as readonly number[]).includes(p.bwKHz)) {
    problems.push({ field: 'bwKHz', detail: `must be one of ${LORA_BANDWIDTHS_KHZ.join(', ')} kHz` })
  }
  if (!Number.isInteger(p.sf) || p.sf < 5 || p.sf > 12) {
    problems.push({ field: 'sf', detail: 'spreading factor must be 5 to 12' })
  }
  if (!Number.isInteger(p.cr) || p.cr < 5 || p.cr > 8) {
    problems.push({ field: 'cr', detail: 'coding rate must be 5 to 8 (4/5 to 4/8)' })
  }
  if (Number.isFinite(p.freqMHz) && Number.isFinite(p.bwKHz) && !isInsideBand(p.region, p.freqMHz, p.bwKHz)) {
    const [lo, hi] = channelEdges(p.freqMHz, p.bwKHz)
    problems.push({
      field: 'freqMHz',
      detail: `${lo.toFixed(4)}-${hi.toFixed(4)} MHz is outside ${region.displayName} (${region.bandMhz[0]}-${region.bandMhz[1]} MHz)`,
    })
  }
  return problems
}

export function findProfile(id: string, extra: readonly LoRaNetworkProfile[] = []): LoRaNetworkProfile | undefined {
  return [...extra, ...BUILT_IN_PROFILES].find((p) => p.id === id)
}

export function defaultProfile(): MeshCoreProfile {
  return BUILT_IN_PROFILES.find((p) => p.id === DEFAULT_PROFILE_ID) as MeshCoreProfile
}

/** Short human description: "909.875 MHz / 62.5 kHz / SF9 / CR5". */
export function describeProfile(p: LoRaNetworkProfile): string {
  if (p.firmware === 'meshtastic') return `${p.meshtasticRegion} · ${p.modemPreset.replace('_', ' ')}`
  return `${p.freqMHz} MHz / ${p.bwKHz} kHz / SF${p.sf} / CR${p.cr}`
}

/** Does a MeshCore radio reporting these values already match the profile? */
export function radioMatches(p: MeshCoreProfile, r: { freqMHz: number; bwKHz: number; sf: number; cr: number }): boolean {
  return (
    Math.round(r.freqMHz * 1000) === Math.round(p.freqMHz * 1000) &&
    Math.round(r.bwKHz * 1000) === Math.round(p.bwKHz * 1000) &&
    r.sf === p.sf &&
    r.cr === p.cr
  )
}
